// The Curve tab's editor, its pure part (spec clip-speed-curves-tab): a curve over a clip's file
// span as places (0 to 1) and speeds, drawn with speed up on a log scale; a point dragged, added at
// the playhead or taken away, each held to the contract's rules. Pure: no React, no DOM.
import { MAX_SPEED, MAX_SPEED_POINTS, MIN_SPEED, SPEED_POINT_GAP_MS, speedAt, type CurvePoint } from "./speed-curve";

/** The speeds the editor marks, top to bottom: halvings from 4× to 0.25×. */
export const SPEED_MARKS = [4, 2, 1, 0.5, 0.25] as const;

/** Where a speed sits from the top of a box `height` tall: 4× at 0, 0.25× at `height`, by halvings. */
export function speedY(speed: number, height: number): number {
  return ((2 - Math.log2(speed)) / 4) * height;
}

/** The speed at `y` of a box `height` tall, in hundredths and within 0.25× to 4×. */
export function speedOfY(y: number, height: number): number {
  const speed = 2 ** (2 - (4 * Math.min(height, Math.max(0, y))) / Math.max(1, height));
  return Math.round(Math.min(MAX_SPEED, Math.max(MIN_SPEED, speed)) * 100) / 100;
}

/** The least distance between two points' places: the contract's 10 ms of a file span. */
export function nearestGap(spanMs: number): number {
  return spanMs > 0 ? SPEED_POINT_GAP_MS / spanMs : 1;
}

/**
 * The curve with point `index` moved to `at` and `speed`: the first and last stay at 0 and 1,
 * moving only up and down; any other is held between its neighbours, `gap` from each.
 */
export function dragPoint(points: readonly CurvePoint[], index: number, at: number, speed: number, gap: number): CurvePoint[] {
  const held = Math.round(Math.min(MAX_SPEED, Math.max(MIN_SPEED, speed)) * 100) / 100;
  return points.map((each, which) => {
    if (which !== index) return each;
    if (index === 0 || index === points.length - 1) return { at: each.at, speed: held };
    const low = points[index - 1]!.at + gap;
    const high = points[index + 1]!.at - gap;
    return { at: low > high ? each.at : Math.min(high, Math.max(low, at)), speed: held };
  });
}

/**
 * The curve with a point added at `at`, at the curve's speed there, or null where none may go:
 * outside the span, within `gap` of a point, or past the contract's 16.
 */
export function addPoint(points: readonly CurvePoint[], at: number, gap: number): CurvePoint[] | null {
  if (points.length >= MAX_SPEED_POINTS || at <= 0 || at >= 1) return null;
  if (points.some((each) => Math.abs(each.at - at) < gap)) return null;
  const added = { at, speed: Math.round(speedAt(points, at) * 100) / 100 };
  return [...points, added].sort((one, other) => one.at - other.at);
}

/** The curve without point `index`, or null for its first and last, which hold the span's ends. */
export function removePoint(points: readonly CurvePoint[], index: number): CurvePoint[] | null {
  if (index <= 0 || index >= points.length - 1) return null;
  return points.filter((_, at) => at !== index);
}
